import type {
  AdminInstallationContract,
  DashboardContract,
  InstrumentQueueItemContract,
  Locale,
  MessageKey,
  ObligationActionInputContract,
  ObligationActionKindContract,
  ObligationActionResultContract,
  ObligationContract,
  ObligationFamilyContract,
  OperationalPaymentContract,
  OperationalReportContract,
  SchoolSettingsContract,
  SchoolAdvanceYearInputContract,
  SchoolAdvanceYearResultContract,
  SchoolSettingsInputContract,
  SystemStatusContract,
  StudentProfileContract,
  StudentServiceAmountsInputContract,
  StudentServiceAmountsResultContract,
} from "@chams-pay/contracts";
import { downloadFinancialDocumentPdf, renderDocumentToPng } from "@chams-pay/document-ui";
import { jsPDF } from "jspdf";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { apiRequestFailureKind, deviceFetch, json } from "./client.js";
import { casablancaDate, dashboardPeriodPresetBounds, monthBounds } from "./format.js";
import { usePreparedWhatsAppIntent, type PreparedWhatsAppIntent } from "./use-whatsapp-intent.js";

/* ------------------------------------------------------------------ */
/* Dashboard                                                           */
/* ------------------------------------------------------------------ */

function operationFailureBodyKey(reason: unknown, timeoutKey: MessageKey): MessageKey {
  const kind = apiRequestFailureKind(reason);
  if (kind === "authentication") return "ops.authenticationFailedBody";
  if (kind === "authorization") return "ops.authorizationFailedBody";
  if (kind === "transport") return "ops.networkFailedBody";
  if (kind === "server") return "ops.serverFailedBody";
  if (kind === "timeout") return timeoutKey;
  return "ops.responseFailedBody";
}

export interface DashboardController {
  readonly from: string;
  readonly to: string;
  readonly dashboard: DashboardContract | null;
  readonly busy: boolean;
  readonly failed: boolean;
  readonly failureBodyKey: MessageKey;
  readonly setAcademicYear: (value: string) => void;
  readonly setFrom: (value: string) => void;
  readonly setTo: (value: string) => void;
  readonly reload: () => Promise<void>;
}

export function useDashboard(): DashboardController {
  const today = casablancaDate();
  const initialPeriod = dashboardPeriodPresetBounds("week", undefined, today);
  const [from, setFrom] = useState(initialPeriod.from);
  const [to, setTo] = useState(initialPeriod.to);
  const [academicYear, setAcademicYear] = useState<string | null>(null);
  const [dashboard, setDashboard] = useState<DashboardContract | null>(null);
  const [failed, setFailed] = useState(false);
  const [failureBodyKey, setFailureBodyKey] = useState<MessageKey>("dash.failedBody");
  const [busy, setBusy] = useState(false);

  const reload = useCallback(async () => {
    setBusy(true);
    setFailed(false);
    try {
      const query = new URLSearchParams({ from, to });
      if (academicYear) query.set("academicYear", academicYear);
      setDashboard(
        await json<DashboardContract>(
          await deviceFetch(`/api/operations/dashboard?${query.toString()}`),
        ),
      );
    } catch (reason) {
      setFailureBodyKey(operationFailureBodyKey(reason, "dash.failedBody"));
      setFailed(true);
    } finally {
      setBusy(false);
    }
  }, [academicYear, from, to]);

  useEffect(() => {
    void reload();
  }, [reload]);

  return {
    from,
    to,
    dashboard,
    busy,
    failed,
    failureBodyKey,
    setAcademicYear,
    setFrom,
    setTo,
    reload,
  };
}

/* ------------------------------------------------------------------ */
/* Cheques and transfers                                               */
/* ------------------------------------------------------------------ */

export type InstrumentAction = "settle" | "reject";

/** The three buckets an operator reasons about, mapped from the queue status. */
export type InstrumentStatusFilter = "pending" | "settled" | "rejected";

export function instrumentBucket(
  status: InstrumentQueueItemContract["status"],
): InstrumentStatusFilter {
  if (status === "pending") return "pending";
  if (status === "rejected") return "rejected";
  return "settled";
}

export interface InstrumentsController {
  readonly items: readonly InstrumentQueueItemContract[];
  /** Presentation only: the client-side status filter over the loaded list. */
  readonly filter: InstrumentStatusFilter;
  readonly setFilter: (value: InstrumentStatusFilter) => void;
  readonly visible: readonly InstrumentQueueItemContract[];
  readonly availableFilters: readonly InstrumentStatusFilter[];
  readonly visibleTotalMad: string;
  readonly pendingCount: number;
  /** Scoped failures: the list load and the transition fail independently. */
  readonly loadFailed: boolean;
  readonly actionFailed: boolean;
  readonly selected: InstrumentQueueItemContract | null;
  readonly action: InstrumentAction;
  readonly statementDate: string;
  readonly statementReference: string;
  readonly reason: string;
  readonly busy: boolean;
  readonly failed: boolean;
  readonly saved: boolean;
  readonly submitDisabled: boolean;
  readonly begin: (item: InstrumentQueueItemContract, action: InstrumentAction) => void;
  readonly dismiss: () => void;
  readonly setStatementDate: (value: string) => void;
  readonly setStatementReference: (value: string) => void;
  readonly setReason: (value: string) => void;
  readonly reload: () => Promise<void>;
  readonly submit: () => Promise<void>;
}

export function useInstruments(
  csrf: string,
  {
    initialFilter = "pending",
    onFilterChange,
    onTransitioned,
  }: {
    readonly initialFilter?: InstrumentStatusFilter;
    readonly onFilterChange?: (value: InstrumentStatusFilter) => void;
    readonly onTransitioned?: (action: InstrumentAction) => void;
  } = {},
): InstrumentsController {
  const [items, setItems] = useState<readonly InstrumentQueueItemContract[]>([]);
  const [selected, setSelected] = useState<InstrumentQueueItemContract | null>(null);
  const [action, setAction] = useState<InstrumentAction>("settle");
  const [statementDate, setStatementDate] = useState(casablancaDate());
  const [statementReference, setStatementReference] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [actionFailed, setActionFailed] = useState(false);
  const [saved, setSaved] = useState(false);
  const [filter, setFilterState] = useState<InstrumentStatusFilter>(initialFilter);
  const setFilter = useCallback(
    (value: InstrumentStatusFilter) => {
      setFilterState(value);
      onFilterChange?.(value);
    },
    [onFilterChange],
  );

  const reload = useCallback(async () => {
    setFailed(false);
    setLoadFailed(false);
    try {
      const payload = await json<{ items: InstrumentQueueItemContract[] }>(
        await deviceFetch("/api/operations/instruments"),
      );
      setItems(payload.items);
    } catch {
      setFailed(true);
      setLoadFailed(true);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  const submit = useCallback(async () => {
    if (!selected) return;
    setBusy(true);
    setFailed(false);
    setActionFailed(false);
    try {
      const response = await deviceFetch(
        `/api/operations/instruments/${selected.paymentId}/transition`,
        {
          method: "POST",
          headers: { "content-type": "application/json", "x-csrf-token": csrf },
          body: JSON.stringify({
            action,
            bankStatementDate: statementDate,
            ...(statementReference.trim() ? { bankStatementReference: statementReference } : {}),
            ...(action === "reject" ? { reason } : {}),
          }),
        },
      );
      if (!response.ok) await json(response);
      setSelected(null);
      setSaved(true);
      setReason("");
      setStatementReference("");
      onTransitioned?.(action);
      await reload();
    } catch {
      setFailed(true);
      setActionFailed(true);
    } finally {
      setBusy(false);
    }
  }, [action, csrf, onTransitioned, reason, reload, selected, statementDate, statementReference]);

  const visible = useMemo(
    () => items.filter((item) => instrumentBucket(item.status) === filter),
    [filter, items],
  );
  const availableFilters = useMemo(
    () =>
      (["pending", "settled", "rejected"] as const).filter((bucket) =>
        items.some((item) => instrumentBucket(item.status) === bucket),
      ),
    [items],
  );
  const visibleTotalMad = useMemo(
    () => visible.reduce((total, item) => total + BigInt(item.amountMad), 0n).toString(),
    [visible],
  );

  return {
    items,
    filter,
    setFilter,
    visible,
    availableFilters,
    visibleTotalMad,
    pendingCount: items.filter((item) => item.status === "pending").length,
    loadFailed,
    actionFailed,
    selected,
    action,
    statementDate,
    statementReference,
    reason,
    busy,
    failed,
    saved,
    submitDisabled: busy || (action === "reject" && reason.trim().length < 3),
    begin: useCallback((item: InstrumentQueueItemContract, next: InstrumentAction) => {
      setAction(next);
      setSelected(item);
    }, []),
    dismiss: useCallback(() => setSelected(null), []),
    setStatementDate,
    setStatementReference,
    setReason,
    reload,
    submit,
  };
}

/* ------------------------------------------------------------------ */
/* Reports                                                             */
/* ------------------------------------------------------------------ */

export type ReportMode = "daily" | "monthly";

export interface ReportsController {
  readonly mode: ReportMode;
  readonly date: string;
  readonly month: string;
  readonly report: OperationalReportContract | null;
  readonly busy: boolean;
  readonly failed: boolean;
  readonly failureBodyKey: MessageKey;
  readonly setMode: (mode: ReportMode) => void;
  readonly setDate: (value: string) => void;
  readonly setMonth: (value: string) => void;
  readonly reload: () => Promise<void>;
  readonly print: () => void;
  readonly download: () => Promise<void>;
}

async function waitForReportAssets(root: Document | HTMLElement): Promise<void> {
  const ownerDocument =
    root.nodeType === 9 ? (root as Document) : (root as HTMLElement).ownerDocument;
  await ownerDocument.fonts.ready;
  const images = [...root.querySelectorAll<HTMLImageElement>("img")];
  await Promise.all(
    images.map(async (image) => {
      if (!image.complete) {
        await new Promise<void>((resolve) => {
          image.addEventListener("load", () => resolve(), { once: true });
          image.addEventListener("error", () => resolve(), { once: true });
        });
      }
      await image.decode().catch(() => undefined);
    }),
  );
}

function visibleOperationalReport(): HTMLElement | null {
  const reports = [...document.querySelectorAll<HTMLElement>(".operational-report")];
  return reports.filter((candidate) => candidate.getClientRects().length > 0).at(-1) ?? null;
}

function printOperationalReport(filename: string): void {
  const report = visibleOperationalReport();
  if (!report) return;

  const title = filename.replace(/\.pdf$/i, "");
  const originalTitle = document.title;
  const frame = document.createElement("iframe");
  frame.title = report.lang === "ar" ? "طباعة التقرير" : "Impression du rapport";
  frame.style.cssText =
    "position:fixed;inset-block-start:0;inset-inline-start:-10000px;width:210mm;height:297mm;border:0;pointer-events:none";
  const cleanup = () => {
    document.title = originalTitle;
    frame.remove();
  };

  frame.addEventListener(
    "load",
    () => {
      const frameWindow = frame.contentWindow;
      const frameDocument = frame.contentDocument;
      if (!frameWindow || !frameDocument) {
        cleanup();
        return;
      }

      void waitForReportAssets(frameDocument).then(() => {
        frameDocument.title = title;
        document.title = title;
        frameWindow.onafterprint = cleanup;
        frameWindow.focus();
        frameWindow.print();
        window.setTimeout(cleanup, 60_000);
      });
    },
    { once: true },
  );

  const styles = [...document.head.querySelectorAll("style, link[rel='stylesheet']")]
    .map((node) => node.outerHTML)
    .join("\n");
  frame.srcdoc = `<!doctype html>
<html lang="${report.lang}" dir="${report.dir}">
<head>
  <meta charset="utf-8">
  <title>${title}</title>
  <base href="${document.baseURI}">
  ${styles}
</head>
<body>${report.outerHTML}</body>
</html>`;
  document.body.append(frame);
}

async function downloadOperationalReportPdf(filename: string): Promise<void> {
  const report = visibleOperationalReport();
  if (!report) return;
  const sourcePages = [...report.querySelectorAll<HTMLElement>(".operational-report__page")];
  if (sourcePages.length === 0) return;

  const host = document.createElement("div");
  host.style.cssText =
    "position:fixed;inset-block-start:0;inset-inline-start:0;width:180mm;background:#fff;pointer-events:none;z-index:-2147483647";
  document.body.append(host);

  try {
    const pdf = new jsPDF({ format: "a4", orientation: "portrait", unit: "mm" });

    for (const [index, sourcePage] of sourcePages.entries()) {
      const page = sourcePage.cloneNode(true) as HTMLElement;
      page.style.inlineSize = "180mm";
      page.style.blockSize = "267mm";
      page.style.minBlockSize = "267mm";
      page.style.aspectRatio = "auto";
      page.style.margin = "0";
      page.style.borderRadius = "0";
      page.style.boxShadow = "none";
      host.replaceChildren(page);
      const image = await renderDocumentToPng(page);
      if (index > 0) pdf.addPage("a4", "portrait");
      pdf.addImage(image, "PNG", 15, 15, 180, 267, undefined, "FAST");
    }

    pdf.save(filename.endsWith(".pdf") ? filename : `${filename}.pdf`);
  } finally {
    host.remove();
  }
}

export function useReports(): ReportsController {
  const today = casablancaDate();
  const [mode, setMode] = useState<ReportMode>("daily");
  const [date, setDate] = useState(today);
  const [month, setMonth] = useState(today.slice(0, 7));
  const [report, setReport] = useState<OperationalReportContract | null>(null);
  const [busy, setBusy] = useState(true);
  const [failed, setFailed] = useState(false);
  const [failureBodyKey, setFailureBodyKey] = useState<MessageKey>("ops.reportFailedBody");
  const loadSequence = useRef(0);
  const bounds = useMemo(
    () => (mode === "daily" ? { from: date, to: date } : monthBounds(month)),
    [date, mode, month],
  );

  const reload = useCallback(async () => {
    const sequence = ++loadSequence.current;
    setBusy(true);
    setFailed(false);
    setReport(null);
    try {
      const nextReport = await json<OperationalReportContract>(
        await deviceFetch(
          `/api/operations/reports?mode=${mode}&from=${bounds.from}&to=${bounds.to}`,
        ),
      );
      if (sequence === loadSequence.current) setReport(nextReport);
    } catch (reason) {
      if (sequence === loadSequence.current) {
        setFailureBodyKey(operationFailureBodyKey(reason, "ops.reportFailedBody"));
        setFailed(true);
      }
    } finally {
      if (sequence === loadSequence.current) setBusy(false);
    }
  }, [bounds.from, bounds.to, mode]);

  const prepareSelectionChange = useCallback(() => {
    loadSequence.current += 1;
    setBusy(true);
    setFailed(false);
    setReport(null);
  }, []);

  const selectMode = useCallback(
    (value: ReportMode) => {
      if (value === mode) return;
      prepareSelectionChange();
      setMode(value);
    },
    [mode, prepareSelectionChange],
  );

  const selectDate = useCallback(
    (value: string) => {
      if (value === date) return;
      prepareSelectionChange();
      setDate(value);
    },
    [date, prepareSelectionChange],
  );

  const selectMonth = useCallback(
    (value: string) => {
      if (value === month) return;
      prepareSelectionChange();
      setMonth(value);
    },
    [month, prepareSelectionChange],
  );

  useEffect(() => {
    void reload();
  }, [reload]);

  return {
    mode,
    date,
    month,
    report,
    busy,
    failed,
    failureBodyKey,
    setMode: selectMode,
    setDate: selectDate,
    setMonth: selectMonth,
    reload,
    print: useCallback(
      () => printOperationalReport(`report-${mode}-${mode === "daily" ? date : month}.pdf`),
      [date, mode, month],
    ),
    download: useCallback(
      () => downloadOperationalReportPdf(`report-${mode}-${mode === "daily" ? date : month}.pdf`),
      [date, mode, month],
    ),
  };
}

/* ------------------------------------------------------------------ */
/* Obligation adjustments                                              */
/* ------------------------------------------------------------------ */

export interface ObligationActionController {
  readonly kind: ObligationActionKindContract;
  readonly amount: string;
  readonly effectiveDate: string;
  readonly reason: string;
  readonly busy: boolean;
  readonly failed: boolean;
  readonly submitDisabled: boolean;
  readonly setKind: (kind: ObligationActionKindContract) => void;
  readonly setAmount: (value: string) => void;
  readonly setEffectiveDate: (value: string) => void;
  readonly setReason: (value: string) => void;
  readonly submit: () => Promise<void>;
}

export function useObligationAction({
  obligation,
  csrf,
  onSaved,
  onClose,
}: {
  readonly obligation: ObligationContract;
  readonly csrf: string;
  readonly onSaved: (result: ObligationActionResultContract) => void;
  readonly onClose: () => void;
}): ObligationActionController {
  const [kind, setKind] = useState<ObligationActionKindContract>("discount");
  const [amount, setAmount] = useState("");
  const [effectiveDate, setEffectiveDate] = useState(casablancaDate());
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  const submit = useCallback(async () => {
    setBusy(true);
    setFailed(false);
    try {
      const body: ObligationActionInputContract = {
        kind,
        effectiveDate,
        reason: reason.trim(),
        ...(kind === "waiver" ? {} : { amountMad: amount.trim() }),
      };
      onSaved(
        await json<ObligationActionResultContract>(
          await deviceFetch(`/api/operations/obligations/${obligation.id}/actions`, {
            method: "POST",
            headers: { "content-type": "application/json", "x-csrf-token": csrf },
            body: JSON.stringify(body),
          }),
        ),
      );
      setAmount("");
      setReason("");
      onClose();
    } catch {
      setFailed(true);
    } finally {
      setBusy(false);
    }
  }, [amount, csrf, effectiveDate, kind, obligation.id, onClose, onSaved, reason]);

  return {
    kind,
    amount,
    effectiveDate,
    reason,
    busy,
    failed,
    submitDisabled:
      busy || reason.trim().length < 3 || (kind !== "waiver" && !/^-?\d+$/.test(amount.trim())),
    setKind,
    setAmount,
    setEffectiveDate,
    setReason,
    submit,
  };
}

const studentServiceFamilies = [
  "registration",
  "tuition",
  "transport",
  "accompaniment",
] as const satisfies readonly ObligationFamilyContract[];

export type StudentServiceAmountValues = Record<ObligationFamilyContract, string>;

export function studentServiceAmountEnabled(
  serviceSummaryFr: string,
  yearRole: "historical" | "current" | "next" | undefined,
  family: ObligationFamilyContract,
): boolean {
  return (
    yearRole === "historical" ||
    family === "registration" ||
    family === "tuition" ||
    (family === "transport" && /transport/i.test(serviceSummaryFr)) ||
    (family === "accompaniment" && /accompagnement/i.test(serviceSummaryFr))
  );
}

function serviceAmountValues(
  student: StudentProfileContract,
  calendarSnapshotId: string,
): StudentServiceAmountValues {
  return Object.fromEntries(
    studentServiceFamilies.map((family) => {
      const obligations = student.obligations.filter(
        (obligation) =>
          obligation.family === family && obligation.calendarSnapshotId === calendarSnapshotId,
      );
      const active = obligations.filter((obligation) => obligation.remainingMad !== "0");
      const activeAmounts = new Set(
        active.map((obligation) => obligation.selectionMad ?? obligation.netMad),
      );
      return [
        family,
        activeAmounts.size === 1
          ? (active[0]!.selectionMad ?? active[0]!.netMad)
          : active.length > 0
            ? ""
            : (obligations[0]?.selectionMad ?? obligations[0]?.netMad ?? ""),
      ];
    }),
  ) as StudentServiceAmountValues;
}

export interface StudentServiceAmountsController {
  readonly academicYears: NonNullable<StudentProfileContract["academicYears"]>;
  readonly calendarSnapshotId: string;
  readonly values: StudentServiceAmountValues;
  readonly enabled: Readonly<Record<ObligationFamilyContract, boolean>>;
  readonly available: Readonly<Record<ObligationFamilyContract, boolean>>;
  readonly reason: string;
  readonly busy: boolean;
  readonly failed: boolean;
  readonly saved: boolean;
  readonly submitDisabled: boolean;
  readonly setAmount: (family: ObligationFamilyContract, value: string) => void;
  readonly setCalendarSnapshotId: (value: string) => void;
  readonly setReason: (value: string) => void;
  readonly submit: () => Promise<void>;
}

export function useStudentServiceAmounts({
  student,
  csrf,
  onSaved,
}: {
  readonly student: StudentProfileContract;
  readonly csrf: string;
  readonly onSaved: (result: StudentServiceAmountsResultContract) => void;
}): StudentServiceAmountsController {
  const academicYears = useMemo(
    () =>
      (
        student.academicYears ?? [
          {
            academicYear: student.calendar.academicYear,
            calendarSnapshotId: student.calendar.id,
            activeFrom: student.calendar.activeFrom,
            activeThrough: student.calendar.activeThrough,
            role: "current" as const,
          },
        ]
      ).filter((year) =>
        student.obligations.some(
          (obligation) =>
            obligation.calendarSnapshotId === year.calendarSnapshotId &&
            obligation.remainingMad !== "0",
        ),
      ),
    [student.academicYears, student.obligations],
  );
  const defaultCalendar =
    academicYears.find((year) => year.role === "current")?.calendarSnapshotId ??
    academicYears[0]?.calendarSnapshotId ??
    student.calendar.id;
  const [calendarSnapshotId, setCalendarSnapshotId] = useState(defaultCalendar);
  const initial = useMemo(
    () => serviceAmountValues(student, calendarSnapshotId),
    [calendarSnapshotId, student],
  );
  const enabled = useMemo(() => {
    const selectedYear = academicYears.find(
      (year) => year.calendarSnapshotId === calendarSnapshotId,
    );
    return Object.fromEntries(
      studentServiceFamilies.map((family) => [
        family,
        studentServiceAmountEnabled(student.serviceSummaryFr, selectedYear?.role, family),
      ]),
    ) as Record<ObligationFamilyContract, boolean>;
  }, [academicYears, calendarSnapshotId, student.serviceSummaryFr]);
  const available = useMemo(
    () =>
      Object.fromEntries(
        studentServiceFamilies.map((family) => [
          family,
          enabled[family] &&
            student.obligations.some(
              (obligation) =>
                obligation.family === family &&
                obligation.calendarSnapshotId === calendarSnapshotId &&
                obligation.remainingMad !== "0",
            ),
        ]),
      ) as Record<ObligationFamilyContract, boolean>,
    [calendarSnapshotId, enabled, student.obligations],
  );
  const [values, setValues] = useState<StudentServiceAmountValues>(initial);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setValues(initial);
    setReason("");
    setFailed(false);
    setSaved(false);
  }, [initial]);

  const changedFamilies = studentServiceFamilies.filter(
    (family) => available[family] && values[family] !== initial[family],
  );
  const validAmounts = changedFamilies.every((family) => /^\d+$/.test(values[family]));

  const submit = useCallback(async () => {
    if (changedFamilies.length === 0 || !validAmounts || reason.trim().length < 3) return;
    setBusy(true);
    setFailed(false);
    setSaved(false);
    try {
      const amountsMad = Object.fromEntries(
        changedFamilies.map((family) => [family, values[family]]),
      ) as StudentServiceAmountsInputContract["amountsMad"];
      onSaved(
        await json<StudentServiceAmountsResultContract>(
          await deviceFetch(`/api/operations/students/${student.id}/service-amounts`, {
            method: "POST",
            headers: { "content-type": "application/json", "x-csrf-token": csrf },
            body: JSON.stringify({
              amountsMad,
              calendarSnapshotId,
              effectiveDate: casablancaDate(),
              reason: reason.trim(),
            } satisfies StudentServiceAmountsInputContract),
          }),
        ),
      );
      setSaved(true);
    } catch {
      setFailed(true);
    } finally {
      setBusy(false);
    }
  }, [
    calendarSnapshotId,
    changedFamilies,
    csrf,
    onSaved,
    reason,
    student.id,
    validAmounts,
    values,
  ]);

  return {
    academicYears,
    calendarSnapshotId,
    values,
    enabled,
    available,
    reason,
    busy,
    failed,
    saved,
    submitDisabled:
      busy || changedFamilies.length === 0 || !validAmounts || reason.trim().length < 3,
    setAmount: useCallback(
      (family: ObligationFamilyContract, value: string) =>
        setValues((current) => ({ ...current, [family]: value })),
      [],
    ),
    setCalendarSnapshotId,
    setReason,
    submit,
  };
}

/* ------------------------------------------------------------------ */
/* Synchronized payment: receipt sharing and cancellation              */
/* ------------------------------------------------------------------ */

export interface OperationalPaymentController {
  readonly official: boolean;
  readonly shareable: boolean;
  readonly receiptLocale: Locale;
  readonly actionBusy: boolean;
  readonly actionFailed: boolean;
  readonly showCancellation: boolean;
  readonly cancellationDate: string;
  readonly cancellationReason: string;
  readonly confirmed: boolean;
  readonly cancelDisabled: boolean;
  readonly setReceiptLocale: (locale: Locale) => void;
  readonly setShowCancellation: (value: boolean) => void;
  readonly setCancellationDate: (value: string) => void;
  readonly setCancellationReason: (value: string) => void;
  readonly setConfirmed: (value: boolean) => void;
  readonly download: () => Promise<void>;
  readonly whatsapp: PreparedWhatsAppIntent;
  readonly cancelPayment: () => Promise<void>;
}

export function useOperationalPayment({
  payment,
  csrf,
  onClose,
  onChanged,
}: {
  readonly payment: OperationalPaymentContract | null;
  readonly csrf: string;
  readonly onClose: () => void;
  readonly onChanged: () => void;
}): OperationalPaymentController {
  const paymentId = payment?.payment.id ?? null;
  const [receiptLanguage, setReceiptLanguage] = useState<{
    readonly paymentId: string | null;
    readonly locale: Locale;
  }>({ paymentId: null, locale: "ar" });
  const receiptLocale = receiptLanguage.paymentId === paymentId ? receiptLanguage.locale : "ar";
  const setReceiptLocale = useCallback(
    (locale: Locale) => setReceiptLanguage({ paymentId, locale }),
    [paymentId],
  );
  const [actionBusy, setActionBusy] = useState(false);
  const [actionFailed, setActionFailed] = useState(false);
  const [showCancellation, setCancellationVisible] = useState(false);
  const [cancellationDate, setCancellationDate] = useState(casablancaDate());
  const [cancellationReason, setCancellationReason] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const setShowCancellation = useCallback((value: boolean) => {
    setCancellationVisible(value);
    if (!value) setConfirmed(false);
  }, []);

  const official =
    payment !== null &&
    payment.document.kind !== "pending-acknowledgement" &&
    (payment.lifecycle === "settled" || payment.lifecycle === "cancelled");
  const shareable =
    payment !== null &&
    payment.lifecycle !== "cancelled" &&
    payment.lifecycle !== "rejected" &&
    (payment.lifecycle !== "pending" || payment.document.kind === "pending-acknowledgement");
  const whatsapp = usePreparedWhatsAppIntent({
    paymentId,
    locale: receiptLocale,
    csrf,
    enabled: shareable,
  });

  useEffect(() => {
    setCancellationVisible(false);
    setCancellationReason("");
    setConfirmed(false);
    setActionFailed(false);
  }, [payment]);

  const download = useCallback(async () => {
    if (!payment) return;
    setActionFailed(false);
    try {
      await downloadFinancialDocumentPdf(
        `receipt-${payment.payment.reference}-${receiptLocale}.pdf`,
      );
    } catch {
      setActionFailed(true);
    }
  }, [payment, receiptLocale]);

  const cancelPayment = useCallback(async () => {
    if (!payment) return;
    setActionBusy(true);
    setActionFailed(false);
    try {
      const response = await deviceFetch(`/api/operations/payments/${payment.payment.id}/cancel`, {
        method: "POST",
        headers: { "content-type": "application/json", "x-csrf-token": csrf },
        body: JSON.stringify({ cancellationDate, reason: cancellationReason.trim() }),
      });
      if (!response.ok) await json(response);
      onClose();
      onChanged();
    } catch {
      setActionFailed(true);
    } finally {
      setActionBusy(false);
    }
  }, [cancellationDate, cancellationReason, csrf, onChanged, onClose, payment]);

  return {
    official,
    shareable,
    receiptLocale,
    actionBusy,
    actionFailed,
    showCancellation,
    cancellationDate,
    cancellationReason,
    confirmed,
    cancelDisabled: actionBusy || !confirmed || cancellationReason.trim().length < 3,
    setReceiptLocale,
    setShowCancellation,
    setCancellationDate,
    setCancellationReason,
    setConfirmed,
    download,
    whatsapp,
    cancelPayment,
  };
}

/* ------------------------------------------------------------------ */
/* Settings                                                            */
/* ------------------------------------------------------------------ */

export interface SystemStatusController {
  readonly status: SystemStatusContract | null;
  readonly failed: boolean;
  readonly reload: () => void;
}

export function useSystemStatus(): SystemStatusController {
  const [status, setStatus] = useState<SystemStatusContract | null>(null);
  const [failed, setFailed] = useState(false);

  const reload = useCallback(() => {
    setFailed(false);
    setStatus(null);
    void fetch("/api/system-status")
      .then((response) => json<SystemStatusContract>(response))
      .then(setStatus)
      .catch(() => setFailed(true));
  }, []);

  useEffect(reload, [reload]);

  return { status, failed, reload };
}

export interface AccessCodeController {
  readonly identifier: string;
  readonly purpose: string;
  readonly issued: string | null;
  /** Present only when the endpoint returns one; never invented client-side. */
  readonly issuedExpiresAt: string | null;
  readonly busy: boolean;
  readonly setIdentifier: (value: string) => void;
  readonly setPurpose: (value: string) => void;
  readonly issue: () => Promise<void>;
}

export function useAccessCodes(
  csrf: string,
  { onIssued }: { readonly onIssued?: () => void } = {},
): AccessCodeController {
  const [identifier, setIdentifier] = useState("FA-LOCAL-001");
  const [purpose, setPurpose] = useState("enrollment");
  const [issued, setIssued] = useState<string | null>(null);
  const [issuedExpiresAt, setIssuedExpiresAt] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const issue = useCallback(async () => {
    setBusy(true);
    try {
      const result = await json<{ code: string; expiresAt?: string }>(
        await deviceFetch("/api/admin/access-codes", {
          method: "POST",
          headers: { "content-type": "application/json", "x-csrf-token": csrf },
          body: JSON.stringify({ userIdentifier: identifier, purpose }),
        }),
      );
      setIssued(result.code);
      setIssuedExpiresAt(result.expiresAt ?? null);
      onIssued?.();
    } finally {
      setBusy(false);
    }
  }, [csrf, identifier, onIssued, purpose]);

  return {
    identifier,
    purpose,
    issued,
    issuedExpiresAt,
    busy,
    setIdentifier,
    setPurpose,
    issue,
  };
}

export interface InstallationRequestsController {
  readonly items: readonly AdminInstallationContract[];
  readonly loading: boolean;
  readonly failed: boolean;
  readonly busyId: string | null;
  readonly reload: () => void;
  readonly approve: (id: string) => Promise<void>;
  readonly reject: (id: string) => Promise<void>;
  readonly revoke: (id: string) => Promise<void>;
}

export function useInstallationRequests(
  csrf: string,
  {
    onResolved,
  }: {
    readonly onResolved?: (action: "authorize" | "reject" | "revoke") => void;
  } = {},
): InstallationRequestsController {
  const [items, setItems] = useState<readonly AdminInstallationContract[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    const value = await json<{ items: readonly AdminInstallationContract[] }>(
      await deviceFetch("/api/admin/installations"),
    );
    setItems(value.items);
  }, []);

  const reload = useCallback(() => {
    setLoading(true);
    setFailed(false);
    void load()
      .catch(() => setFailed(true))
      .finally(() => setLoading(false));
  }, [load]);

  useEffect(reload, [reload]);

  const resolve = useCallback(
    async (id: string, action: "authorize" | "reject" | "revoke") => {
      setBusyId(id);
      setFailed(false);
      try {
        await json(
          await deviceFetch(`/api/admin/installations/${id}/${action}`, {
            method: "POST",
            headers: { "x-csrf-token": csrf },
          }),
        );
        await load();
        onResolved?.(action);
      } catch {
        setFailed(true);
      } finally {
        setBusyId(null);
      }
    },
    [csrf, load, onResolved],
  );

  return {
    items,
    loading,
    failed,
    busyId,
    reload,
    approve: useCallback((id: string) => resolve(id, "authorize"), [resolve]),
    reject: useCallback((id: string) => resolve(id, "reject"), [resolve]),
    revoke: useCallback((id: string) => resolve(id, "revoke"), [resolve]),
  };
}

export interface SchoolSettingsController {
  readonly settings: SchoolSettingsContract | null;
  readonly draft: SchoolSettingsInputContract | null;
  readonly advanceYearDraft: SchoolAdvanceYearInputContract | null;
  readonly loading: boolean;
  readonly saving: boolean;
  readonly activatingAdvanceYear: boolean;
  readonly saved: boolean;
  readonly failed: boolean;
  readonly advanceYearFailed: boolean;
  readonly advanceYearResult: SchoolAdvanceYearResultContract | null;
  readonly setDraft: (value: SchoolSettingsInputContract) => void;
  readonly setAdvanceYearDraft: (value: SchoolAdvanceYearInputContract) => void;
  readonly reload: () => void;
  readonly save: () => Promise<void>;
  readonly activateAdvanceYear: () => Promise<boolean>;
}

function nextSchoolYear(value: string): { readonly academicYear: string } | null {
  const match = /^(\d{4})-(\d{4})$/.exec(value);
  if (!match) return null;
  const next = Number(match[2]);
  if (next !== Number(match[1]) + 1) return null;
  return {
    academicYear: `${next}-${next + 1}`,
  };
}

function settingsDraft(value: SchoolSettingsContract): SchoolSettingsInputContract {
  return {
    schoolNameAr: value.schoolNameAr,
    schoolNameFr: value.schoolNameFr,
    email: value.email,
    phone: value.phone,
    addressAr: value.addressAr,
    addressFr: value.addressFr,
    amountsMad: { ...value.amountsMad },
    classes: value.classes.map((entry) => ({ ...entry })),
  };
}

export function useSchoolSettings(
  csrf: string,
  {
    onSaved,
  }: {
    readonly onSaved?: () => void;
  } = {},
): SchoolSettingsController {
  const [settings, setSettings] = useState<SchoolSettingsContract | null>(null);
  const [draft, setDraft] = useState<SchoolSettingsInputContract | null>(null);
  const [advanceYearDraft, setAdvanceYearDraft] = useState<SchoolAdvanceYearInputContract | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activatingAdvanceYear, setActivatingAdvanceYear] = useState(false);
  const [saved, setSaved] = useState(false);
  const [failed, setFailed] = useState(false);
  const [advanceYearFailed, setAdvanceYearFailed] = useState(false);
  const [advanceYearResult, setAdvanceYearResult] =
    useState<SchoolAdvanceYearResultContract | null>(null);

  const applySettings = useCallback((value: SchoolSettingsContract) => {
    setSettings(value);
    setDraft(settingsDraft(value));
    const next = nextSchoolYear(value.academicYear);
    setAdvanceYearDraft(
      value.advanceYearAvailable && next && !value.advanceYear
        ? { academicYear: next.academicYear, amountsMad: { ...value.amountsMad } }
        : null,
    );
  }, []);

  const reload = useCallback(() => {
    setLoading(true);
    setFailed(false);
    setAdvanceYearFailed(false);
    void deviceFetch("/api/admin/school-settings")
      .then((response) => json<SchoolSettingsContract>(response))
      .then(applySettings)
      .catch(() => setFailed(true))
      .finally(() => setLoading(false));
  }, [applySettings]);

  useEffect(reload, [reload]);

  const save = useCallback(async () => {
    if (!draft) return;
    setSaving(true);
    setSaved(false);
    setFailed(false);
    try {
      const value = await json<SchoolSettingsContract>(
        await deviceFetch("/api/admin/school-settings", {
          method: "PUT",
          headers: { "content-type": "application/json", "x-csrf-token": csrf },
          body: JSON.stringify(draft),
        }),
      );
      setSettings(value);
      setDraft(settingsDraft(value));
      setSaved(true);
      onSaved?.();
    } catch {
      setFailed(true);
    } finally {
      setSaving(false);
    }
  }, [csrf, draft, onSaved]);

  const activateAdvanceYear = useCallback(async () => {
    if (!advanceYearDraft) return false;
    setActivatingAdvanceYear(true);
    setFailed(false);
    setAdvanceYearFailed(false);
    setAdvanceYearResult(null);
    const requested = advanceYearDraft;
    try {
      const result = await json<SchoolAdvanceYearResultContract>(
        await deviceFetch("/api/admin/school-settings/advance-year", {
          method: "POST",
          headers: { "content-type": "application/json", "x-csrf-token": csrf },
          body: JSON.stringify(advanceYearDraft),
        }),
      );
      applySettings(result.settings);
      setAdvanceYearResult(result);
      return true;
    } catch (reason) {
      const kind = apiRequestFailureKind(reason);
      if (kind !== "authentication" && kind !== "authorization") {
        try {
          const value = await json<SchoolSettingsContract>(
            await deviceFetch("/api/admin/school-settings"),
          );
          if (value.advanceYear?.academicYear === requested.academicYear) {
            applySettings(value);
            setAdvanceYearResult({
              settings: value,
              eligibleStudents: value.advanceYear.eligibleStudents,
              obligationsCreated: value.advanceYear.obligationsCreated,
            });
            return true;
          }
        } catch {
          /* Preserve the specific activation failure below if reconciliation also fails. */
        }
      }
      setAdvanceYearFailed(true);
      return false;
    } finally {
      setActivatingAdvanceYear(false);
    }
  }, [advanceYearDraft, applySettings, csrf]);

  return {
    settings,
    draft,
    advanceYearDraft,
    loading,
    saving,
    activatingAdvanceYear,
    saved,
    failed,
    advanceYearFailed,
    advanceYearResult,
    setDraft,
    setAdvanceYearDraft,
    reload,
    save,
    activateAdvanceYear,
  };
}
